import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Zap, CheckCircle2, ArrowRight, Sparkles, X } from "lucide-react";

const FLOW = ["AI Detect", "AI Recommend", "Human Approve", "Publish to Meta", "Monitor", "Learn"];
const RISK = { low: "bg-accent/15 text-accent", medium: "bg-primary/15 text-primary", high: "bg-destructive/15 text-destructive" };
const TYPE = { creative: "Creative", budget: "Budget", pause: "Pause", scale: "Scale" };

export default function AiOptimize() {
  const [recs, setRecs] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/ai/recommendations").then((r) => setRecs(r.data)).catch(() => setRecs([]));
  useEffect(() => { load(); }, []);

  const approve = async (rec) => {
    setBusy(rec.id);
    try { await api.post(`/ai/recommendations/${rec.id}/approve`); toast.success(`Approved & published: ${rec.campaign}`); await load(); }
    catch (e) { toast.error("Approve failed"); }
    finally { setBusy(null); }
  };
  const dismiss = async (rec) => {
    setBusy(rec.id);
    try { await api.post(`/ai/recommendations/${rec.id}/dismiss`); toast.info("Recommendation dismissed"); await load(); }
    catch (e) { toast.error("Dismiss failed"); }
    finally { setBusy(null); }
  };

  if (!recs) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 animate-float-up" data-testid="optimize-panel">
      <div className="bg-card border border-border rounded-2xl p-5">
        <p className="text-xs font-mono uppercase tracking-widest text-primary mb-3">AI never changes budgets or publishes without approval</p>
        <div className="flex flex-wrap items-center gap-2">
          {FLOW.map((s, i) => (
            <React.Fragment key={s}>
              <span className={`px-3 py-1.5 rounded-lg text-xs font-mono font-semibold ${i === 2 ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>{s}</span>
              {i < FLOW.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {recs.map((r) => (
        <div key={r.id} data-testid={`rec-${r.id}`} className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{TYPE[r.type] || r.type}</span>
              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${RISK[r.risk]}`}>{r.risk} risk</span>
              <span className="font-display font-semibold">{r.campaign}</span>
            </div>
            {r.status === "executed" && <span data-testid={`rec-executed-${r.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-accent"><CheckCircle2 className="w-4 h-4" /> Executed · Published to Meta</span>}
            {r.status === "dismissed" && <span className="text-xs font-semibold text-muted-foreground">Dismissed</span>}
          </div>

          <div className="mt-3 space-y-2 text-sm">
            <p><span className="text-xs font-mono uppercase tracking-widest text-destructive mr-2">Detect</span>{r.detect}</p>
            <p><span className="text-xs font-mono uppercase tracking-widest text-primary mr-2">Recommend</span>{r.recommendation}</p>
            <p className="text-accent text-xs font-mono">Impact: {r.impact}</p>
          </div>

          {r.status === "pending" && (
            <div className="flex gap-2 mt-4">
              <Button data-testid={`rec-approve-${r.id}`} onClick={() => approve(r)} disabled={busy === r.id} className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold">
                {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" fill="currentColor" /> Approve & Publish</>}
              </Button>
              <Button data-testid={`rec-dismiss-${r.id}`} variant="outline" onClick={() => dismiss(r)} disabled={busy === r.id} className="rounded-xl"><X className="w-4 h-4 mr-1" /> Dismiss</Button>
            </div>
          )}
          {r.status === "executed" && r.approved_by && <p className="text-[11px] text-muted-foreground mt-3">Approved by {r.approved_by}</p>}
        </div>
      ))}
    </div>
  );
}
